import { Grid, SelectChangeEvent } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  UPCOMING_COURSE_TYPE,
} from "utils/constants/constant";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  allowSearch,
  getPagePayloadValuesWithId,
  getPagePermissions,
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
  toggleFilter,
} from "utils/helper";
import { useHistory } from "react-router-dom";
import {
  IPastCourses,
  IStudentDashboardListMeta,
  IStudentDashboardPagePayloadValues,
  IUpcomingCourse,
  IUpcomingCourseFilter,
} from "utils/interfaces/student-dashboard";
import { UpcomingCourseListSuccessPayload } from "store/studentDashboard/types";
import { useSelector } from "react-redux";
import StudentCourseTable from "components/StudentCoursesTable";
import { IRoleModulePermission } from "store/role/types";
import { AppPages } from "utils/enums/app-pages";
import { SCOBlendedCourse } from "utils/enums/scheduled-offering";
import StudentCoursesFilter from "../StudentCoursesFilter";

const UpcomingCoursesList: React.FC<any> = (props) => {
  const history = useHistory();
  const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const user = useSelector((state: any) => state?.auth?.user);
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [orderBy, setOrderBy] = useState<keyof IPastCourses>("courseName");
  const [filteredData, setFilteredData] = useState<IUpcomingCourseFilter>();
  const [pagePermission, setPagePermission] = useState<IRoleModulePermission>();
  const [upcomingCoursesList, setUpcomingCoursesList] =
    useState<IUpcomingCourse[]>();
  const [filterBox, setFilterBox] = useState(false);
  const [studentDashboardListMeta, setStudentDashboardListMeta] =
    useState<IStudentDashboardListMeta>();
  const [searchText, setSearchText] = useState<string>("");

  const handleSearch = (event: any) => {
    setSearchText(event?.target?.value);
    setFilteredData((prevState: any) => ({
      ...prevState,
      name: event?.target?.value,
    }));
  };

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event?.target?.value as string);
    setPageNo(1);
  };

  const onPageChange = (e: any, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = React.useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof IPastCourses) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const onUpcomingCoursesListSuccess = (
    response: UpcomingCourseListSuccessPayload
  ) => {
    setUpcomingCoursesList(response?.upcomingCourseList);
    setStudentDashboardListMeta(response?.upcomingListPagination);
    hideLoaderForBatchApiCall();
  };

  const getUpcomingCoursesList = async (filterData?: IUpcomingCourseFilter) => {
    const { upcomingCourseListRequest } = props;
    if (upcomingCourseListRequest) {
      showLoaderForBatchApiCall();
      const values: IStudentDashboardPagePayloadValues =
        getPagePayloadValuesWithId(
          user?.studentId,
          orderBy,
          order,
          pageNo,
          page
        );
      if (filterData?.name) {
        values.name = filterData?.name;
      }
      if (filterData?.term) {
        values.term = filterData?.term;
      }
      if (filterData?.locationName) {
        values.locationName = filterData?.locationName;
      }
      if (
        filterData?.isBlendedCourse === SCOBlendedCourse.No ||
        filterData?.isBlendedCourse === SCOBlendedCourse.Yes
      ) {
        values.isBlendedCourse = filterData?.isBlendedCourse;
      }
      if (filterData) {
        setFilteredData(filterData);
      } else {
        setFilteredData(undefined);
      }
      const payload = {
        values,
        callback: onUpcomingCoursesListSuccess,
      };
      upcomingCourseListRequest(payload);
    }
  };

  const handleFilter = () => {
    setFilterBox(true);
    document.body.classList.toggle("filter-open");
  };

  const handleFilterApplyClick = (formData: IUpcomingCourseFilter) => {
    setFilteredData(formData);
    setSearchText(formData?.name);
    if (pageNo === DEFAULT_PAGE_NO) {
      getUpcomingCoursesList(formData);
    } else {
      setPageNo(1);
    }
    setFilterBox(false);
    toggleFilter();
  };

  const handleSearchKeyDown = (e: any) => {
    if (allowSearch(e)) {
      setPageNo(1);
      getUpcomingCoursesList(filteredData);
    }
  };

  const clearSearchText = () => {
    setSearchText("");
    setFilteredData((prevFilteredData: any) => ({
      ...prevFilteredData,
      name: "",
    }));
    getUpcomingCoursesList({
      name: "",
      locationName: filteredData?.locationName ? filteredData?.locationName : 0,
      term: filteredData?.term ? filteredData?.term : 0,
    });
  };

  const clearFilter = () => {
    setSearchText("");
    setFilteredData((prevFilteredData: any) => ({
      ...prevFilteredData,
      name: "",
    }));
  };

  useEffect(() => {
    const permission = getPagePermissions(
      user?.permissions,
      AppPages.StudentDashboard,
      "student"
    );
    setPagePermission(permission);
    if (permission?.view) {
      if (filteredData) {
        getUpcomingCoursesList(filteredData);
      } else {
        getUpcomingCoursesList();
      }
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, order, orderBy, page]);

  return (
    <Grid item xs={12}>
      <StudentCourseTable
        coursesList={upcomingCoursesList}
        pagePermission={pagePermission}
        courseListMeta={studentDashboardListMeta}
        handleRequestSort={handleRequestSort}
        order={order}
        orderBy={orderBy}
        handleChange={handleChange}
        onPageChange={onPageChange}
        page={page}
        pageNo={pageNo}
        courseListType={UPCOMING_COURSE_TYPE}
        handleFilter={handleFilter}
        searchText={searchText}
        handleSearch={handleSearch}
        handleSearchKeyDown={handleSearchKeyDown}
        handleClearSearch={clearSearchText}
        isUpComingCourse
      />
      {filterBox && (
        <StudentCoursesFilter
          filterBox={filterBox}
          setFilterBox={setFilterBox}
          handleApplyClick={handleFilterApplyClick}
          filteredData={filteredData}
          pageNo={pageNo}
          setPageNo={setPageNo}
          setFilteredData={setFilteredData}
          getUpcomingCoursesList={getUpcomingCoursesList}
          clearSearchText={clearFilter}
        />
      )}
    </Grid>
  );
};

export default UpcomingCoursesList;
